/**
 * Paste + publish formatting for the rich text composer.
 *
 * Clipboard HTML from Docs, Word, and the web carries spacing and styles as
 * inline CSS. The editor used to drop anything outside a small schema, and the
 * article page then forced one margin on every paragraph. This module keeps
 * the safe slice of that formatting so a paste looks like the source.
 */

const BLOCK_PROPS = new Set([
  "margin-top",
  "margin-bottom",
  "margin-left",
  "margin-right",
  "line-height",
  "text-align",
  "text-indent",
  "padding-left",
]);

const INLINE_PROPS = new Set([
  "color",
  "background-color",
  "font-size",
  "font-family",
  "font-weight",
  "font-style",
  "text-decoration",
  "text-decoration-line",
  "line-height",
  "letter-spacing",
  "white-space",
]);

export const RICH_TEXT_TAGS = [
  "p",
  "br",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "strike",
  "del",
  "ul",
  "ol",
  "li",
  "span",
  "h2",
  "h3",
  "h4",
  "blockquote",
  "a",
] as const;

const ALLOWED_TAGS = new Set<string>(RICH_TEXT_TAGS);

const BLOCK_TAGS = new Set([
  "p",
  "div",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "ul",
  "ol",
  "li",
  "blockquote",
  "table",
  "tr",
  "td",
  "th",
  "section",
  "article",
  "header",
  "footer",
  "pre",
  "figure",
]);

type Decl = { prop: string; value: string };

function allowedPropsFor(tag: string): Set<string> {
  if (tag === "span") return INLINE_PROPS;
  if (
    tag === "p" ||
    tag === "li" ||
    tag === "blockquote" ||
    tag === "h1" ||
    tag === "h2" ||
    tag === "h3" ||
    tag === "h4" ||
    tag === "h5" ||
    tag === "h6" ||
    tag === "ul" ||
    tag === "ol" ||
    tag === "div"
  ) {
    return BLOCK_PROPS;
  }
  return new Set();
}

function parseDeclarations(style: string): Decl[] {
  const decls: Decl[] = [];
  if (!style) return decls;
  for (const part of style.split(";")) {
    const idx = part.indexOf(":");
    if (idx === -1) continue;
    const prop = part.slice(0, idx).trim().toLowerCase();
    let value = part.slice(idx + 1).trim().replace(/\s*!important\s*$/i, "").trim();
    if (!prop || !value) continue;
    if (prop === "margin") {
      decls.push(...expandMargin(value));
      continue;
    }
    decls.push({ prop, value });
  }
  return decls;
}

function expandMargin(value: string): Decl[] {
  const parts = value.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return [];
  const top = parts[0];
  const right = parts[1] ?? parts[0];
  const bottom = parts.length >= 3 ? parts[2] : parts[0];
  const left = parts.length === 4 ? parts[3] : parts.length >= 2 ? parts[1] : parts[0];
  return [
    { prop: "margin-top", value: top },
    { prop: "margin-right", value: right },
    { prop: "margin-bottom", value: bottom },
    { prop: "margin-left", value: left },
  ];
}

function isSafeValue(value: string): boolean {
  if (!value || value.length > 160) return false;
  if (/url\s*\(|expression\s*\(|javascript:|@import|\/\*|behavior\s*:|-moz-binding/i.test(value)) return false;
  return /^[#a-zA-Z0-9%.,\s'"()\-+!/]+$/.test(value);
}

function isNoop(prop: string, value: string): boolean {
  if (prop === "background-color" && /^transparent$/i.test(value)) return true;
  if (prop === "font-weight" && /^(normal|400)$/i.test(value)) return true;
  if (prop === "font-style" && /^normal$/i.test(value)) return true;
  if ((prop === "text-decoration" || prop === "text-decoration-line") && /^none$/i.test(value)) return true;
  if (prop === "white-space" && !/pre/i.test(value)) return true;
  if (/^(inherit|initial|unset|revert)$/i.test(value)) return true;
  return false;
}

function isZeroLength(value: string): boolean {
  return /^(0|0px|0pt|0em|0rem|0%)$/i.test(value.trim());
}

function serialize(decls: Decl[]): string {
  const seen = new Map<string, string>();
  for (const decl of decls) {
    if (!isSafeValue(decl.value) || isNoop(decl.prop, decl.value)) continue;
    if ((decl.prop === "margin-left" || decl.prop === "margin-right") && isZeroLength(decl.value)) continue;
    seen.set(decl.prop, decl.value);
  }
  return Array.from(seen, ([prop, value]) => `${prop}: ${value}`).join("; ");
}

function pick(style: string, allowed: Set<string>): string {
  return serialize(parseDeclarations(style).filter((decl) => allowed.has(decl.prop)));
}

/** Keep only safe CSS for a tag. Used when pasting and when publishing. */
export function sanitizeStyleAttribute(style: string, tag: string): string {
  return pick(style, allowedPropsFor(tag.toLowerCase()));
}

function escapeAttr(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/"/g, "'").replace(/</g, "");
}

function safeHref(href: string): string | null {
  const trimmed = href.trim().replace(/[\u0000-\u001f\s]+/g, "");
  if (!trimmed || trimmed.length > 2000) return null;
  if (/^(javascript|data|vbscript):/i.test(trimmed)) return null;
  if (trimmed.startsWith("/") && !trimmed.startsWith("//")) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^mailto:/i.test(trimmed)) return trimmed;
  return null;
}

/** Server-safe allowlist for admin-authored HTML (blog posts, and the same tags the composer emits). */
export function sanitizeRichTextHtml(html: string): string {
  if (!html) return "";
  let out = html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    .replace(/(href|src)\s*=\s*(['"]?)\s*javascript:[^'"\s>]*/gi, "");

  out = out.replace(/<\/?([a-z0-9]+)([^>]*)>/gi, (full, rawTag: string, rawAttrs: string) => {
    const tag = rawTag.toLowerCase();
    const closing = full.startsWith("</");
    if (!ALLOWED_TAGS.has(tag)) return "";
    if (closing) return `</${tag}>`;
    if (tag === "br") return "<br>";
    if (tag === "a") {
      const hrefMatch = /href\s*=\s*("([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(rawAttrs);
      const href = safeHref(hrefMatch?.[2] || hrefMatch?.[3] || hrefMatch?.[4] || "");
      if (!href) return "";
      const external = /^https?:/i.test(href);
      const extra = external ? ` target="_blank" rel="noopener noreferrer"` : "";
      return `<a href="${escapeAttr(href)}"${extra}>`;
    }
    const styleMatch = /style\s*=\s*("([^"]*)"|'([^']*)')/i.exec(rawAttrs);
    const style = sanitizeStyleAttribute(styleMatch?.[2] || styleMatch?.[3] || "", tag);
    return style ? `<${tag} style="${escapeAttr(style)}">` : `<${tag}>`;
  });

  return out;
}

function renameElement(el: Element, tag: string): HTMLElement {
  const next = el.ownerDocument.createElement(tag);
  for (const attr of el.getAttributeNames()) {
    next.setAttribute(attr, el.getAttribute(attr) || "");
  }
  while (el.firstChild) next.appendChild(el.firstChild);
  el.replaceWith(next);
  return next;
}

function wrap(el: Element, tag: string): void {
  const wrapper = el.ownerDocument.createElement(tag);
  el.replaceWith(wrapper);
  wrapper.appendChild(el);
}

function removeNodes(root: ParentNode, selector: string): void {
  for (const node of Array.from(root.querySelectorAll(selector))) node.remove();
}

function removeComments(root: Node): void {
  const walker = root.ownerDocument?.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
  if (!walker) return;
  const comments: Comment[] = [];
  while (walker.nextNode()) comments.push(walker.currentNode as Comment);
  for (const comment of comments) comment.remove();
}

function convertFonts(root: ParentNode): void {
  for (const font of Array.from(root.querySelectorAll("font"))) {
    const span = font.ownerDocument.createElement("span");
    const parts: string[] = [];
    const color = font.getAttribute("color");
    const face = font.getAttribute("face");
    if (color) parts.push(`color: ${color}`);
    if (face) parts.push(`font-family: ${face}`);
    const existing = font.getAttribute("style");
    if (existing) parts.push(existing);
    if (parts.length) span.setAttribute("style", parts.join("; "));
    while (font.firstChild) span.appendChild(font.firstChild);
    font.replaceWith(span);
  }
}

function unwrapFakeFormatting(root: ParentNode): void {
  const nodes = Array.from(root.querySelectorAll("b, strong, i, em, u, s, strike, del"));
  for (const el of nodes) {
    const style = el.getAttribute("style") || "";
    const weight = /font-weight\s*:\s*([^;]+)/i.exec(style)?.[1]?.trim() || "";
    const fontStyle = /font-style\s*:\s*([^;]+)/i.exec(style)?.[1]?.trim() || "";
    const deco = /text-decoration(?:-line)?\s*:\s*([^;]+)/i.exec(style)?.[1]?.trim() || "";
    const tag = el.tagName.toLowerCase();
    const fakeBold = (tag === "b" || tag === "strong") && /normal|^400$/.test(weight);
    const fakeItalic = (tag === "i" || tag === "em") && /^normal$/.test(fontStyle);
    const fakeLine = (tag === "u" || tag === "s" || tag === "strike" || tag === "del") && /^none$/.test(deco);
    if (fakeBold || fakeItalic || fakeLine) el.replaceWith(...el.childNodes);
  }
}

function flattenTables(root: ParentNode): void {
  for (const table of Array.from(root.querySelectorAll("table")).reverse()) {
    const frag = table.ownerDocument.createDocumentFragment();
    for (const row of Array.from(table.querySelectorAll("tr"))) {
      const cells = Array.from(row.children).filter((cell) => /^(td|th)$/i.test(cell.tagName));
      if (cells.length === 0) continue;
      const p = table.ownerDocument.createElement("p");
      cells.forEach((cell, index) => {
        if (index > 0) p.appendChild(table.ownerDocument.createTextNode("  "));
        while (cell.firstChild) p.appendChild(cell.firstChild);
      });
      frag.appendChild(p);
    }
    table.replaceWith(frag);
  }
}

function convertDivs(root: ParentNode): void {
  const divs = Array.from(root.querySelectorAll("div, section, article, header, footer, figure"));
  for (const div of divs.reverse()) {
    const hasBlock = Array.from(div.children).some((child) => BLOCK_TAGS.has(child.tagName.toLowerCase()));
    if (hasBlock) {
      div.replaceWith(...div.childNodes);
      continue;
    }
    const p = div.ownerDocument.createElement("p");
    const style = div.getAttribute("style");
    if (style) p.setAttribute("style", style);
    while (div.firstChild) p.appendChild(div.firstChild);
    div.replaceWith(p);
  }
}

function mapHeadings(root: ParentNode): void {
  for (const heading of Array.from(root.querySelectorAll("h1, h5, h6"))) {
    const tag = heading.tagName.toLowerCase();
    renameElement(heading, tag === "h1" ? "h2" : "h4");
  }
}

const INLINE_HOSTS = new Set(["STRONG", "B", "EM", "I", "U", "S", "STRIKE", "DEL", "A", "SPAN"]);

function applyInline(parent: HTMLElement, inline: string): void {
  const doc = parent.ownerDocument;
  for (const child of Array.from(parent.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      if (!child.textContent) continue;
      const span = doc.createElement("span");
      span.setAttribute("style", inline);
      child.replaceWith(span);
      span.appendChild(child);
      continue;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) continue;
    const el = child as HTMLElement;
    if (el.tagName === "BR") continue;
    if (el.tagName === "SPAN") {
      const merged = serialize([
        ...parseDeclarations(inline),
        ...parseDeclarations(el.getAttribute("style") || ""),
      ]);
      if (merged) el.setAttribute("style", merged);
      continue;
    }
    if (INLINE_HOSTS.has(el.tagName)) applyInline(el, inline);
  }
}

function sinkInlineStyles(root: ParentNode): void {
  // Innermost inline tags first so a span keeps its own font and only inherits what it lacks.
  const hosts = Array.from(root.querySelectorAll("b, strong, em, i, u, s, strike, del, a")).reverse();
  for (const host of hosts) {
    if (!(host instanceof HTMLElement)) continue;
    const inline = pick(host.getAttribute("style") || "", INLINE_PROPS);
    if (!inline) continue;
    host.removeAttribute("style");
    applyInline(host, inline);
  }

  const blocks = Array.from(root.querySelectorAll("p, h2, h3, h4, li, blockquote"));
  for (const block of blocks) {
    if (!(block instanceof HTMLElement)) continue;
    const style = block.getAttribute("style") || "";
    const inline = pick(
      style,
      new Set([...INLINE_PROPS].filter((prop) => prop !== "line-height")),
    );
    const blockStyle = pick(style, BLOCK_PROPS);
    if (blockStyle) block.setAttribute("style", blockStyle);
    else block.removeAttribute("style");
    if (inline) applyInline(block, inline);
  }
}

function weightIsBold(value: string): boolean {
  if (/bold/i.test(value)) return true;
  const n = Number(value);
  return Number.isFinite(n) && n >= 600;
}

function promoteSemanticStyles(root: ParentNode): void {
  const spans = Array.from(root.querySelectorAll("span")).reverse();
  for (const span of spans) {
    const decls = parseDeclarations(span.getAttribute("style") || "");
    const weight = decls.find((decl) => decl.prop === "font-weight")?.value || "";
    const fontStyle = decls.find((decl) => decl.prop === "font-style")?.value || "";
    const deco = decls
      .filter((decl) => decl.prop === "text-decoration" || decl.prop === "text-decoration-line")
      .map((decl) => decl.value)
      .join(" ");
    const keep = decls.filter(
      (decl) =>
        decl.prop !== "font-weight" &&
        decl.prop !== "font-style" &&
        decl.prop !== "text-decoration" &&
        decl.prop !== "text-decoration-line",
    );
    const next = serialize(keep);
    if (next) span.setAttribute("style", next);
    else span.removeAttribute("style");
    if (/underline/i.test(deco)) wrap(span, "u");
    if (/line-through/i.test(deco)) wrap(span, "s");
    if (/italic|oblique/i.test(fontStyle)) wrap(span, "em");
    if (weightIsBold(weight)) wrap(span, "strong");
    if (!span.getAttribute("style")) span.replaceWith(...span.childNodes);
  }
}

function unwrapDisallowed(root: HTMLElement): void {
  let guard = 0;
  while (guard < 50) {
    guard += 1;
    const bad = Array.from(root.querySelectorAll("*")).find((el) => !ALLOWED_TAGS.has(el.tagName.toLowerCase()));
    if (!bad) break;
    bad.replaceWith(...bad.childNodes);
  }
}

function cleanAttributes(root: ParentNode): void {
  for (const el of Array.from(root.querySelectorAll("*"))) {
    const tag = el.tagName.toLowerCase();
    if (tag === "a") {
      const href = safeHref(el.getAttribute("href") || "");
      for (const attr of el.getAttributeNames()) el.removeAttribute(attr);
      if (!href) {
        el.replaceWith(...el.childNodes);
        continue;
      }
      el.setAttribute("href", href);
      if (/^https?:/i.test(href)) {
        el.setAttribute("target", "_blank");
        el.setAttribute("rel", "noopener noreferrer");
      }
      continue;
    }
    const style = sanitizeStyleAttribute(el.getAttribute("style") || "", tag);
    for (const attr of el.getAttributeNames()) el.removeAttribute(attr);
    if (style) el.setAttribute("style", style);
  }
}

function fillEmptyParagraphs(root: ParentNode): void {
  for (const p of Array.from(root.querySelectorAll("p"))) {
    const text = (p.textContent || "").replace(/\u00a0/g, "").trim();
    // Leave the paragraph empty. The editor draws one line for that, and the
    // published page adds a break. A <br> here becomes a second blank line.
    if (!text) p.replaceChildren();
  }
}

function preserveRepeatedSpaces(root: ParentNode): void {
  const doc = root.ownerDocument;
  if (!doc) return;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  while (walker.nextNode()) nodes.push(walker.currentNode as Text);
  for (const node of nodes) {
    const value = node.nodeValue || "";
    node.nodeValue = value
      .replace(/\t/g, "    ")
      .replace(/ {2,}/g, (run) => "\u00A0".repeat(run.length))
      .replace(/^ +/, (run) => "\u00A0".repeat(run.length));
  }
}

/**
 * Turn clipboard HTML into markup the composer can keep: headings, lists,
 * links, colors, fonts, and the source paragraph spacing.
 */
export function normalizePastedHtml(html: string): string {
  if (!html || !html.includes("<")) return html;
  if (typeof DOMParser === "undefined") return html;
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const root = doc.body;
    removeNodes(root, "script, style, meta, link, title, iframe, object, embed, svg, img, video, audio, canvas");
    removeComments(root);
    convertFonts(root);
    unwrapFakeFormatting(root);
    flattenTables(root);
    convertDivs(root);
    mapHeadings(root);
    sinkInlineStyles(root);
    promoteSemanticStyles(root);
    unwrapDisallowed(root);
    cleanAttributes(root);
    fillEmptyParagraphs(root);
    preserveRepeatedSpaces(root);
    return root.innerHTML;
  } catch {
    return html;
  }
}
